import React, { forwardRef } from "react";
import { Upload, X, Trash2, AlertCircle } from "lucide-react";

const ProductImages = forwardRef(
  (
    {
      mainImages,
      setMainImages,
      currentImages,
      setCurrentImages,
      updateImagesToDelete,
      imagesToDelete,
      setImagesToDelete,
      formErrors = {},
      setFormErrors = () => {},
      showToast = () => {}, // Fonction pour afficher les toasts, passée en prop
    },
    ref
  ) => {
    // Gestion du changement d'image
    const handleImageChange = (e, key) => {
      if (e.target.files && e.target.files[0]) {
        // Mettre à jour mainImages avec le nouveau fichier
        setMainImages((prev) => ({
          ...prev,
          [key]: e.target.files[0],
        }));

        // Si cette image était marquée pour suppression, la retirer de la liste
        if (imagesToDelete.includes(key)) {
          setImagesToDelete((prev) => prev.filter((item) => item !== key));
        }

        // Si c'est l'image1, effacer l'erreur potentielle
        if (key === "image1") {
          setFormErrors((prev) => ({ ...prev, image1: null }));
        }
      }
    };

    // Gestion de la suppression d'image
    const handleImageDelete = (key) => {
      // Ne pas permettre la suppression de image1 si c'est obligatoire et qu'il n'y a pas de remplacement
      if (key === "image1" && !mainImages[key] && currentImages[key]) {
        // Si on tente de supprimer l'image1 existante sans remplacement
        setFormErrors((prev) => ({
          ...prev,
          image1: "La première image est obligatoire",
        }));

        // Afficher un toast d'erreur
        showToast({
          type: "error",
          message:
            "La première image est obligatoire et ne peut pas être supprimée sans remplacement.",
        });

        return;
      }

      // Ajouter l'image à la liste des images à supprimer
      setImagesToDelete((prev) => [...prev, key]);

      // Si un fichier a été sélectionné mais pas encore envoyé, le supprimer de l'état
      if (mainImages[key]) {
        setMainImages((prev) => {
          const newImages = { ...prev };
          delete newImages[key];
          return newImages;
        });
      }

      // Si c'est l'image1, ajouter une erreur
      if (key === "image1" && !mainImages[key]) {
        setFormErrors((prev) => ({
          ...prev,
          image1: "La première image est obligatoire",
        }));

        // Afficher un toast d'erreur
        showToast({
          type: "error",
          message:
            "La première image est obligatoire. Veuillez ajouter une image.",
        });
      }
    };

    // Gestion de l'annulation de suppression
    const handleCancelDelete = (key) => {
      setImagesToDelete((prev) => prev.filter((item) => item !== key));

      // Si c'est l'image1, effacer l'erreur potentielle
      if (key === "image1") {
        setFormErrors((prev) => ({ ...prev, image1: null }));
      }
    };

    // Vérifier si la première image est présente
    const isFirstImagePresent = () => {
      return (
        (mainImages["image1"] && !imagesToDelete.includes("image1")) ||
        (currentImages["image1"] && !imagesToDelete.includes("image1"))
      );
    };

    // Préparation des données pour le formulaire avant envoi
    const validateImages = () => {
      const errors = {};

      // Vérifier si l'image1 est présente
      if (!isFirstImagePresent()) {
        errors.image1 = "La première image est obligatoire";
      }

      return errors;
    };

    // Fonction qui peut être appelée avant la soumission du formulaire
    const validateBeforeSubmit = () => {
      const imageErrors = validateImages();
      setFormErrors((prev) => ({ ...prev, ...imageErrors }));

      if (imageErrors.image1) {
        // Afficher un toast d'erreur lors de la validation du formulaire
        showToast({
          type: "error",
          message:
            "La première image est obligatoire pour enregistrer le produit.",
        });
      }

      return Object.keys(imageErrors).length === 0;
    };

    // Exposer la fonction de validation via ref
    React.useImperativeHandle(ref, () => ({
      validateBeforeSubmit,
    }));

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {["image1", "image2", "image3"].map((key, index) => (
            <div key={key} className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Image principale {index + 1}
                {key === "image1" && (
                  <span className="text-red-500 ml-1">*</span>
                )}
              </label>
              <div className="flex items-center justify-center w-full">
                <label
                  className={`relative w-full h-64 flex flex-col items-center justify-center border-2 ${
                    formErrors[key]
                      ? "border-red-500"
                      : imagesToDelete.includes(key)
                      ? "border-red-300 bg-red-50"
                      : key === "image1" && !isFirstImagePresent()
                      ? "border-red-300"
                      : "border-dashed"
                  } rounded-lg cursor-pointer hover:bg-gray-50`}
                >
                  {/* Afficher le message de suppression si l'image est marquée pour suppression */}
                  {imagesToDelete.includes(key) ? (
                    <div className="flex flex-col items-center justify-center text-center p-4">
                      <Trash2 className="w-8 h-8 text-red-500 mb-2" />
                      <p className="text-sm text-red-500 font-medium">
                        Cette image sera supprimée
                      </p>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          handleCancelDelete(key);
                        }}
                        className="mt-3 px-3 py-1 bg-gray-200 text-gray-700 rounded-md text-xs"
                      >
                        Annuler
                      </button>
                    </div>
                  ) : mainImages[key] ? (
                    // Image nouvellement sélectionnée
                    <img
                      src={URL.createObjectURL(mainImages[key])}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-full object-contain"
                    />
                  ) : currentImages[key] ? (
                    // Image existante
                    <img
                      src={currentImages[key]}
                      alt={`Current ${index + 1}`}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    // Aucune image
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload className="w-8 h-8 text-gray-400" />
                      <p className="mt-2 text-sm text-gray-500">
                        {key === "image1"
                          ? "Cliquez pour ajouter (obligatoire)"
                          : "Cliquez pour ajouter"}
                      </p>
                    </div>
                  )}

                  {/* Input file caché */}
                  <input
                    type="file"
                    className="hidden"
                    onChange={(e) => handleImageChange(e, key)}
                    accept="image/*"
                    disabled={imagesToDelete.includes(key)}
                    required={key === "image1"}
                  />

                  {/* Bouton de suppression pour les images existantes ou nouvellement sélectionnées */}
                  {(mainImages[key] || currentImages[key]) &&
                    !imagesToDelete.includes(key) && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleImageDelete(key);
                        }}
                        className={`absolute top-2 right-2 p-1 ${
                          key === "image1" && !mainImages[key]
                            ? "bg-gray-400" // Grisé si c'est image1 obligatoire sans remplacement
                            : "bg-red-500 hover:bg-red-600"
                        } text-white rounded-full`}
                        title={
                          key === "image1" && !mainImages[key]
                            ? "La première image est obligatoire"
                            : "Supprimer cette image"
                        }
                        disabled={key === "image1" && !mainImages[key]}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                </label>
              </div>
              {formErrors[key] && (
                <div className="flex items-center mt-1 text-red-500 text-sm">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {formErrors[key]}
                </div>
              )}
            </div>
          ))}
        </div>
        {!isFirstImagePresent() && (
          <p className="text-red-500 text-sm mt-2">
            <AlertCircle className="inline w-4 h-4 mr-1" />
            La première image est obligatoire
          </p>
        )}
        {!isFirstImagePresent() && showToast("asdedpwe k")}
      </div>
    );
  }
);

export default ProductImages;
